import { posts, ticker } from "@/mocks/content";
import { badgeOnImage, catOf, textAccent } from "./helpers";

export default function Hero() {
  const lead = posts.find((p) => p.featured) ?? posts[0];
  const pillars = posts.filter((p) => p.pillar).slice(0, 2);

  return (
    <section id="headline" className="w-full">
      <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-10">
        {/* ticker */}
        <div className="mb-8 flex items-stretch overflow-hidden rounded-lg border border-background-200 bg-background-100">
          <div className="z-10 flex shrink-0 items-center gap-2 bg-primary-500 px-4 text-background-50">
            <i className="ri-fire-fill text-sm" aria-hidden="true" />
            <span className="whitespace-nowrap text-xs font-bold tracking-widest">今日爆点</span>
          </div>
          <div className="relative flex-1 overflow-hidden">
            <div className="flex w-max animate-ticker items-center py-2.5">
              {[...ticker, ...ticker].map((t, i) => (
                <span
                  key={`${t}-${i}`}
                  className="flex items-center gap-3 whitespace-nowrap px-6 text-sm text-foreground-700"
                >
                  <i className={`${i % 2 === 0 ? "text-primary-500" : "text-accent-500"} ri-goblet-fill text-xs`} aria-hidden="true" />
                  {t}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* editorial feature grid */}
        <div className="grid gap-5 lg:grid-cols-3">
          {/* lead story */}
          <article className="group relative overflow-hidden rounded-2xl bg-foreground-950 lg:col-span-2">
            <a href="#columns" className="block" aria-label={lead.title}>
              <img
                src={lead.img}
                alt={lead.title}
                title={`${lead.title} - 每日大瓜头版`}
                className="h-[360px] w-full object-cover object-top opacity-95 transition-transform duration-700 group-hover:scale-105 md:h-[520px]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/25 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-5 md:p-8">
                <div className="flex flex-wrap items-center gap-3">
                  <span className={`rounded-md px-2.5 py-1 text-xs font-bold text-background-50 ${badgeOnImage(lead.cat)}`}>
                    {catOf(lead).name}
                  </span>
                  <span className="flex items-center gap-1.5 text-xs text-background-100">
                    <i className="ri-eye-line" aria-hidden="true" /> {lead.reads} 热度
                  </span>
                  <span className="flex items-center gap-1.5 text-xs text-background-100">
                    <i className="ri-chat-3-line" aria-hidden="true" /> {lead.comments.toLocaleString()} 讨论
                  </span>
                </div>
                <h2 className="mt-3 max-w-3xl font-heading text-xl font-black leading-snug text-background-50 md:text-[28px] md:leading-snug">
                  {lead.title}
                </h2>
                <p className="mt-3 hidden max-w-2xl text-sm leading-relaxed text-background-100 md:block">
                  {lead.summary}
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  {lead.tags.map((t) => (
                    <span key={t} className="rounded-full bg-white/15 px-3 py-1 text-xs text-background-50 backdrop-blur">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </a>
          </article>

          {/* pillar stories */}
          <div className="flex flex-col gap-5">
            {pillars.map((p) => (
              <article key={p.id} className="group flex flex-1 overflow-hidden rounded-2xl border border-background-200 bg-background-50">
                <a href="#columns" className="flex w-full gap-4 p-3" aria-label={p.title}>
                  <img
                    src={p.img}
                    alt={p.title}
                    title={`${p.title} - 每日大瓜`}
                    className="h-28 w-28 shrink-0 rounded-xl object-cover object-top md:h-32 md:w-36"
                  />
                  <div className="flex min-w-0 flex-col justify-center py-1">
                    <span className={`text-xs font-semibold ${textAccent(p.cat)}`}>
                      {catOf(p).name}
                    </span>
                    <h3 className="mt-1.5 line-clamp-3 font-heading text-base font-bold leading-snug text-foreground-950 group-hover:text-primary-700">
                      {p.title}
                    </h3>
                    <p className="mt-2 flex items-center gap-2 text-xs text-foreground-500">
                      <span>{p.date}</span>
                      <span className="flex items-center gap-1"><i className="ri-eye-line" aria-hidden="true" />{p.reads}</span>
                    </p>
                  </div>
                </a>
              </article>
            ))}

            {/* stat chip */}
            <div className="flex items-center justify-between rounded-2xl bg-accent-100/70 px-5 py-4">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent-500 text-background-50">
                  <i className="ri-shield-check-line text-lg" aria-hidden="true" />
                </span>
                <div>
                  <p className="text-sm font-bold text-accent-900">多路信源 · 交叉验证</p>
                  <p className="text-xs text-accent-800/80">不轻信单一截图与单一口径</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}